<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllTours"></b-form-checkbox>
        <span @click="checkedAllTours = !checkedAllTours">Komoot Tours</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;"
            :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
    <br>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllPhotos"></b-form-checkbox>
        <span @click="checkedAllPhotos = !checkedAllPhotos">Komoot Tour Photos</span>
        <div style="float: right;">
          <img @click="expandedPhotos = !expandedPhotos" style="height: 13px; cursor: pointer;"
            :src="expandedPhotos ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expandedPhotos">
        <b-form-checkbox class="display-inline marginright5" v-model="activePhotos"></b-form-checkbox>
        <span @click="activePhotos = !activePhotos">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactivePhotos"></b-form-checkbox>
        <span @click="inactivePhotos = !inactivePhotos">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.komoottour-pointer-cursor),
:global(.komoottour-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import Photo from '@/ts/Photo';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasScatterplotLayer } from '@/ts/MapLasScatterplotLayer';
import { MapLasPathLayer } from '@/ts/MapLasPathLayer';
import lightGallery from 'lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgAutoplay from 'lightgallery/plugins/autoplay';

interface KomootTourImage {
  id: number;
  lat: number;
  lon: number;
}

interface KomootTourResponse {
  id: number;
  points: any[];
  images: KomootTourImage[];
  is_active: boolean;
}

interface KomootTourPhoto extends KomootTourImage {
  tourId: number;
  is_active: boolean;
}

interface KomootTourBinaryData {
  length: number;
  attributes: {
    getPosition: { value: Float32Array; size: 2 };
    getFillColor: { value: Uint8Array; size: 4 };
    getLineColor: { value: Uint8Array; size: 4 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface KomootPathBinaryData {
  length: number;
  startIndices: Uint32Array;
  attributes: {
    getPath: { value: Float32Array; size: 2 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface KomootTourCardInstanceState {
  komootToursResult: KomootTourResponse[] | null;
  komootToursProcessed: boolean;
  komootRouteResults: (KomootTourResponse & { normalizedPath: Array<[number, number]> })[];
  komootTourPathLayer: MapLasPathLayer | null;
  komootHoverPolyline: L.Polyline;
  hoveredTourId: number | null;
  komootPhotos: KomootTourPhoto[];
  komootPhotoLayer: MapLasScatterplotLayer | null;
  hoveredTourPhotos: KomootTourPhoto[];
  hoveredTourPhotoLayer: MapLasScatterplotLayer | null;
  deckOverlay: DeckOverlay | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
}

declare module 'vue' {
  interface ComponentCustomProperties extends KomootTourCardInstanceState { }
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAllTours: false,
      expanded: false,
      active: true,
      inactive: false,
      checkedAllPhotos: false,
      expandedPhotos: false,
      activePhotos: true,
      inactivePhotos: false,
      loading: false
    };
  },
  created() {
    this.komootToursResult = null;
    this.komootToursProcessed = false;
    this.komootRouteResults = [];
    this.komootTourPathLayer = null;
    this.komootHoverPolyline = L.polyline([], {
      color: 'yellow',
      weight: 4,
      opacity: 1,
      interactive: false,
    });
    this.hoveredTourId = null;
    this.komootPhotos = [];
    this.komootPhotoLayer = null;
    this.hoveredTourPhotos = [];
    this.hoveredTourPhotoLayer = null;
    this.deckOverlay = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    EventBus.$on('downloadExternalData', () => this.downloadToursKomoot());
    EventBus.$on('toggleKomootTours', () => {
      if (this.komootToursResult) {
        this.checkedAllTours = !this.checkedAllTours
      }
    });
    EventBus.$on('toggleKomootToursPhotos', () => {
      if (this.komootToursResult) {
        this.checkedAllPhotos = !this.checkedAllPhotos
      }
    });
    EventBus.$on('selectedAreaChanged', () => this.onSelectedAreaChanged());
  },
  beforeUnmount() {
    if (window.mainmap && this.mapMouseMoveHandler) window.mainmap.off('mousemove', this.mapMouseMoveHandler);
    if (window.mainmap && this.mapMouseOutHandler) window.mainmap.off('mouseout', this.mapMouseOutHandler);
    if (window.mainmap && this.mapClickHandler) window.mainmap.off('click', this.mapClickHandler);
    this.hideKomootHoverPolyline();
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('komoottour-pointer-cursor');
      if (this.deckOverlay) window.mainmap.removeLayer(this.deckOverlay);
    }
    this.deckOverlay = null;
  },
  methods: {
    isPointInSelectedArea(lat: number | string, lon: number | string) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      const plat = Number(lat);
      const plon = Number(lon);
      if (Number.isNaN(plat) || Number.isNaN(plon)) return false;
      return (plat >= minLat && plat <= maxLat && plon >= minLon && plon <= maxLon);
    },

    isPathInSelectedArea(path: any[]) {
      if (!path || !path.length) return false;
      for (const p of path) {
        if (Array.isArray(p) && p.length >= 2) {
          if (this.isPointInSelectedArea(p[0], p[1])) return true;
        } else if (p && typeof p === 'object') {
          const lat = p.lat ?? p.latitude ?? p[0];
          const lon = p.lon ?? p.lng ?? p.longitude ?? p[1];
          if (lat !== undefined && lon !== undefined && this.isPointInSelectedArea(lat, lon)) return true;
        }
      }
      return false;
    },

    onSelectedAreaChanged() {
      this.hideKomootHoverPolyline();
      this.hoveredTourPhotos = [];
      this.hoveredTourPhotoLayer = null;
      this.komootRouteResults = [];
      this.komootTourPathLayer = null;
      this.komootPhotos = [];
      this.komootPhotoLayer = null;
      this.komootToursProcessed = false;
      if (this.komootToursResult && (this.checkedAllTours || this.checkedAllPhotos)) {
        this.processToursKomoot();
      } else {
        this.updateKomootPhotoLayer();
      }
    },
    downloadToursKomoot() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/komoottours/';
      this.downloadUrlWithCache(endPoint, 'komoottours', 'komoottours.txt', null, true).then((response) => {
        this.komootToursResult = response[1].results;
        if (this.checkedAllTours || this.checkedAllPhotos) this.processToursKomoot();
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('toursDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('toursError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
    processToursKomoot() {
      this.hideKomootHoverPolyline();
      const tours: (KomootTourResponse & { normalizedPath: Array<[number, number]> })[] = [];
      const photos: KomootTourPhoto[] = [];
      for (const responseTour of this.komootToursResult || []) {
        for (const image of responseTour.images || []) {
          if (this.isPointInSelectedArea(image.lat, image.lon)) {
            photos.push({ ...image, tourId: responseTour.id, is_active: responseTour.is_active });
          }
        }
        // include cycleway if any point in it is inside the selected area
        if (this.isPathInSelectedArea(responseTour.points)) {
          const normalizedPath: Array<[number, number]> = [];
          for (const point of responseTour.points || []) {
            const lat = Number(Array.isArray(point) ? point[0] : point?.lat ?? point?.latitude);
            const lon = Number(Array.isArray(point) ? point[1] : point?.lon ?? point?.lng ?? point?.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lon)) normalizedPath.push([lat, lon]);
          }
          if (normalizedPath.length >= 2) tours.push({ ...responseTour, normalizedPath });
        }
      }
      const positions: number[] = [];
      const startIndices: number[] = [];
      const filterValues: number[] = [];
      for (const tour of tours) {
        startIndices.push(positions.length / 2);
        for (const [lat, lon] of tour.normalizedPath) {
          positions.push(lon, lat);
          filterValues.push(tour.is_active ? 1 : 0);
        }
      }
      startIndices.push(positions.length / 2);
      const pathData: KomootPathBinaryData = {
        length: tours.length,
        startIndices: Uint32Array.from(startIndices),
        attributes: {
          getPath: { value: Float32Array.from(positions), size: 2 },
          getFilterValue: { value: Float32Array.from(filterValues), size: 1 },
        },
      };
      this.komootRouteResults = tours;
      this.komootTourPathLayer = new MapLasPathLayer({
        id: 'komoot-tour-paths',
        data: pathData,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getTourStatusFilterRange(),
        positionFormat: 'XY',
        getColor: [255, 0, 0, 255],
        getWidth: 3,
        widthUnits: 'pixels',
        visible: this.checkedAllTours,
        pickable: false,
      });
      this.komootPhotos = photos;
      this.komootToursProcessed = true;
      this.komootPhotoLayer = this.checkedAllPhotos
        ? this.createPhotoDeckLayer(this.komootPhotos, 'komoot-tour-photos', true)
        : null;
      this.onCheckedAllToursChanged();
      this.updateKomootPhotoLayer();
    },
    onCheckedAllToursChanged() {
      this.hideKomootHoverPolyline();
      if (this.checkedAllTours && this.komootToursResult && !this.komootToursProcessed) {
        this.processToursKomoot();
        return;
      }
      if (!this.komootTourPathLayer) return;
      this.komootTourPathLayer.setVisible(this.checkedAllTours);
      this.komootTourPathLayer.setFilterRange(this.getTourStatusFilterRange());
      this.updateKomootPhotoLayer();
    },
    getTourStatusFilterRange(): readonly [number, number] {
      if (this.active && this.inactive) return [0, 1];
      if (this.active) return [1, 1];
      if (this.inactive) return [0, 0];
      return [2, 2];
    },
    createKomootPhotoLayer() {
      this.komootPhotoLayer = this.createPhotoDeckLayer(this.komootPhotos, 'komoot-tour-photos', true);
    },
    createPhotoDeckLayer(photos: KomootTourPhoto[], id: string, filterByStatus: boolean) {
      const positions = new Float32Array(photos.length * 2);
      const fillColors = new Uint8Array(photos.length * 4);
      const lineColors = new Uint8Array(photos.length * 4);
      const filterValues = new Float32Array(photos.length);
      photos.forEach((photo, index) => {
        positions.set([photo.lon, photo.lat], index * 2);
        fillColors.set([0, 0, 255, 51], index * 4);
        lineColors.set([0, 0, 255, 255], index * 4);
        filterValues[index] = photo.is_active ? 1 : 0;
      });
      const data: KomootTourBinaryData = {
        length: photos.length,
        attributes: {
          getPosition: { value: positions, size: 2 },
          getFillColor: { value: fillColors, size: 4 },
          getLineColor: { value: lineColors, size: 4 },
          getFilterValue: { value: filterValues, size: 1 },
        },
      };
      const filterOptions = filterByStatus ? {
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getPhotoStatusFilterRange(),
      } : {};
      return new MapLasScatterplotLayer({
        id,
        data,
        ...filterOptions,
        positionFormat: 'XY',
        _normalize: false,
        getRadius: 8,
        radiusUnits: 'pixels',
        stroked: true,
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: filterByStatus ? this.checkedAllPhotos : true,
        pickable: false,
      });
    },
    showTourPhotos(tourId: number) {
      this.hoveredTourPhotos = this.komootPhotos.filter((photo) => photo.tourId === tourId);
      this.hoveredTourPhotoLayer = this.hoveredTourPhotos.length
        ? this.createPhotoDeckLayer(this.hoveredTourPhotos, 'komoot-tour-hover-photos', false)
        : null;
      this.updateKomootPhotoLayer();
    },
    clearHoveredTourPhotos() {
      this.hoveredTourPhotos = [];
      this.hoveredTourPhotoLayer = null;
      this.updateKomootPhotoLayer();
    },
    getPhotoStatusFilterRange(): readonly [number, number] {
      if (this.activePhotos && this.inactivePhotos) return [0, 1];
      if (this.activePhotos) return [1, 1];
      if (this.inactivePhotos) return [0, 0];
      return [2, 2];
    },
    onPhotoStatusFilterChanged() {
      if (!this.komootPhotoLayer) return;
      this.komootPhotoLayer.setFilterRange(this.getPhotoStatusFilterRange());
      this.updateKomootPhotoLayer();
    },
    onCheckedAllPhotosChanged() {
      if (this.checkedAllPhotos && this.komootToursResult && !this.komootToursProcessed) {
        this.processToursKomoot();
        return;
      }
      if (this.checkedAllPhotos && this.komootToursProcessed && !this.komootPhotoLayer) {
        this.createKomootPhotoLayer();
      } else if (!this.checkedAllPhotos) {
        this.komootPhotoLayer = null;
      }
      this.updateKomootPhotoLayer();
    },
    ensureDeckOverlay() {
      if (!window.mainmap) return;
      if (!this.deckOverlay) {
        this.deckOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.deckOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onKomootMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => this.hideKomootHoverPolyline();
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onKomootMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    onKomootMapMouseMove(event: L.LeafletMouseEvent) {
      let routeIndex = this.checkedAllTours && this.komootTourPathLayer
        ? this.komootTourPathLayer.pick(event.latlng.lat, event.latlng.lng)
        : null;
      if (routeIndex !== null && !this.isKomootTourVisible(this.komootRouteResults[routeIndex])) routeIndex = null;
      if (routeIndex !== null) {
        const tour = this.komootRouteResults[routeIndex];
        window.mainmap.getContainer().classList.add('komoottour-pointer-cursor');
        if (tour && tour.id !== this.hoveredTourId) {
          this.komootHoverPolyline.setLatLngs(tour.normalizedPath);
          this.hoveredTourId = tour.id;
          if (!window.mainmap.hasLayer(this.komootHoverPolyline)) this.komootHoverPolyline.addTo(window.mainmap);
          this.komootHoverPolyline.bringToFront();
          this.showTourPhotos(tour.id);
        }
        return;
      }

      this.hideKomootHoverPolyline();
      const photoIndex = this.checkedAllPhotos && this.komootPhotoLayer
        ? this.komootPhotoLayer.pick(event.latlng.lat, event.latlng.lng)
        : this.hoveredTourPhotoLayer?.pick(event.latlng.lat, event.latlng.lng) ?? null;
      window.mainmap.getContainer().classList.toggle('komoottour-pointer-cursor', photoIndex !== null);
    },
    onKomootMapClick(event: L.LeafletMouseEvent) {
      if (window.isDesktop && this.checkedAllTours && this.komootTourPathLayer) {
        const routeIndex = this.komootTourPathLayer.pick(event.latlng.lat, event.latlng.lng);
        const tour = routeIndex === null ? null : this.komootRouteResults[routeIndex];
        if (tour && this.isKomootTourVisible(tour)) {
          window.open(`https://www.komoot.com/pl-pl/tour/${tour.id}/zoom`, '_blank')?.focus();
          window.open(`https://www.komoot.com/pl-pl/tour/${tour.id}`, '_blank')?.focus();
          return;
        }
      }
      const regularIndex = this.checkedAllPhotos && this.komootPhotoLayer
        ? this.komootPhotoLayer.pick(event.latlng.lat, event.latlng.lng)
        : null;
      const hoveredIndex = regularIndex === null && this.hoveredTourPhotoLayer
        ? this.hoveredTourPhotoLayer.pick(event.latlng.lat, event.latlng.lng)
        : null;
      const photo = regularIndex !== null
        ? this.komootPhotos[regularIndex]
        : hoveredIndex !== null ? this.hoveredTourPhotos[hoveredIndex] : null;
      if (photo) void this.openPhotoGallery(photo);
    },
    hideKomootHoverPolyline() {
      if (window.mainmap) {
        window.mainmap.getContainer().classList.remove('komoottour-pointer-cursor');
        if (window.mainmap.hasLayer(this.komootHoverPolyline)) this.komootHoverPolyline.removeFrom(window.mainmap);
      }
      if (this.hoveredTourId !== null) this.clearHoveredTourPhotos();
      this.hoveredTourId = null;
    },
    isKomootTourVisible(tour: KomootTourResponse | undefined): boolean {
      return tour?.is_active ? this.active : Boolean(tour && this.inactive);
    },
    updateKomootPhotoLayer() {
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;
      if (this.komootPhotoLayer) this.komootPhotoLayer.setVisible(this.checkedAllPhotos);
      if (!this.checkedAllPhotos && !this.hoveredTourPhotoLayer && window.mainmap) {
        window.mainmap.getContainer().classList.remove('komoottour-pointer-cursor');
      }
      const layers = [];
      if (this.komootTourPathLayer) layers.push(this.komootTourPathLayer.getLayer());
      if (this.komootPhotoLayer) layers.push(this.komootPhotoLayer.getLayer());
      if (this.hoveredTourPhotoLayer) layers.push(this.hoveredTourPhotoLayer.getLayer());
      this.deckOverlay.setProps({ layers });
    },
    async openPhotoGallery(image: KomootTourPhoto) {
      const fullImage = `${window.mediaHost}komoot_tour/${image.id}.jpg`;
      const photo = new Photo(image.id, 'Photo', '', String(image.id), null, fullImage, fullImage,
        `${window.mediaHost}komoot_tour/${image.id}_thumb_300.jpg`, false, 1, false);
      await Promise.all([photo.generate_image_fullhd_cache(), photo.generate_image_thumb_cache()]);
      const gallery = lightGallery(document.getElementById('map'), {
        dynamic: true,
        dynamicEl: [{ src: photo.image_fullhd_cache, thumb: photo.image_thumb_cache }],
        plugins: [lgZoom, lgAutoplay],
        showZoomInOutIcons: true,
        slideShowInterval: 3000,
        allowMediaOverlap: true,
        mobileSettings: { controls: false, showCloseIcon: true, download: false },
      });
      gallery.openGallery();
    }
  },
  watch: {
    "checkedAllTours": [{
      handler: "onCheckedAllToursChanged"
    }],
    "active": [{
      handler: "onCheckedAllToursChanged"
    }],
    "inactive": [{
      handler: "onCheckedAllToursChanged"
    }],
    "checkedAllPhotos": [{ handler: "onCheckedAllPhotosChanged" }],
    "activePhotos": [{ handler: "onPhotoStatusFilterChanged" }],
    "inactivePhotos": [{ handler: "onPhotoStatusFilterChanged" }]
  }
})


</script>
